/**
 * ProviderKeysSection — tenant-keys arc (2026-07-13).
 *
 * Settings section where the tenant user manages cloud provider API keys
 * (Anthropic / OpenAI / Google). Keys live server-side in v3_tenant_secrets
 * and are NEVER echoed back — GET /api/provider-keys returns configured
 * booleans only, so this component only ever holds a key in the masked
 * input between typing and Save. After a save the backend live-pushes the
 * key to the tenant LLM router; `router_applied: false` means it becomes
 * active at the next router restart instead (surfaced in the status line).
 *
 * When no key is configured, cloud model requests return the router's
 * honest 503 pointing here — this section is the fix the message names.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

export type ProviderId = 'anthropic' | 'openai' | 'google' | 'openrouter';

const PROVIDERS: Array<{ id: ProviderId; label: string; hint: string }> = [
  { id: 'anthropic', label: 'Anthropic', hint: 'claude-* models' },
  { id: 'openai', label: 'OpenAI', hint: 'gpt-* models' },
  { id: 'google', label: 'Google', hint: 'gemini-* models' },
  { id: 'openrouter', label: 'OpenRouter', hint: 'openrouter/* models' },
];

type ConfiguredMap = Record<ProviderId, boolean>;

interface SaveResult {
  status: string;
  provider: string;
  configured: boolean;
  router_applied: boolean;
}

export function ProviderKeysSection() {
  const [configured, setConfigured] = useState<ConfiguredMap | null>(null);
  const [drafts, setDrafts] = useState<Record<ProviderId, string>>({
    anthropic: '', openai: '', google: '', openrouter: '',
  });
  const [busy, setBusy] = useState<ProviderId | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setConfigured(await api.get<ConfiguredMap>('/provider-keys'));
    } catch {
      setConfigured(null);
      setStatus('Could not load provider key status.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async (provider: ProviderId) => {
    const key = drafts[provider].trim();
    if (!key) return;
    setBusy(provider);
    setStatus(null);
    try {
      const res = await api.put<SaveResult>(`/provider-keys/${provider}`, { key });
      // never keep the key around after a successful save
      setDrafts((d) => ({ ...d, [provider]: '' }));
      setStatus(
        res.router_applied
          ? `${provider} key saved — active now.`
          : `${provider} key saved — active at the next router restart.`,
      );
      await refresh();
    } catch {
      setStatus(`Saving the ${provider} key failed — key unchanged.`);
    } finally {
      setBusy(null);
    }
  };

  const clear = async (provider: ProviderId) => {
    setBusy(provider);
    setStatus(null);
    try {
      await api.delete<SaveResult>(`/provider-keys/${provider}`);
      setStatus(`${provider} key cleared.`);
      await refresh();
    } catch {
      setStatus(`Clearing the ${provider} key failed — key unchanged.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="provider-keys-section">
      <div className="text-sm text-vibe-text">Provider API keys</div>
      <div className="text-xs text-vibe-text-secondary">
        Your own cloud model keys, stored server-side per tenant and never
        shown again after saving. Without a key, cloud models (claude-*,
        gpt-*, gemini-*, openrouter/*) return “no API key configured” instead
        of answers.
      </div>
      {PROVIDERS.map(({ id, label, hint }) => {
        const isConfigured = configured?.[id] === true;
        return (
        <div
          key={id}
          data-testid={`provider-key-row-${id}`}
          className={`flex items-center gap-2 rounded border-l-2 px-2 py-1 transition-colors ${
            isConfigured ? 'border-vibe-success bg-vibe-success/5' : 'border-transparent'
          }`}
        >
          <div className="w-24 shrink-0">
            <div className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                data-testid={`provider-key-dot-${id}`}
                className={`inline-block h-2 w-2 shrink-0 rounded-full ${
                  configured === null
                    ? 'bg-vibe-text-tertiary/40'
                    : isConfigured
                      ? 'bg-vibe-success'
                      : 'bg-vibe-text-tertiary/50'
                }`}
              />
              <div className="text-xs text-vibe-text">{label}</div>
            </div>
            <div
              className={`text-[10px] pl-3.5 ${
                isConfigured ? 'text-vibe-success' : 'text-vibe-text-tertiary'
              }`}
              data-testid={`provider-key-status-${id}`}
            >
              {configured === null ? '…' : isConfigured ? '✓ Configured' : `Not configured — ${hint}`}
            </div>
          </div>
          <input
            type="password"
            autoComplete="off"
            placeholder={configured?.[id] ? 'replace key…' : 'paste key…'}
            value={drafts[id]}
            onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
            data-testid={`provider-key-input-${id}`}
            className="flex-1 min-w-0 px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-xs"
          />
          <button
            type="button"
            onClick={() => void save(id)}
            disabled={busy !== null || !drafts[id].trim()}
            data-testid={`provider-key-save-${id}`}
            className="px-2 py-1 rounded border border-vibe-border text-xs text-vibe-text disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => void clear(id)}
            disabled={busy !== null || configured?.[id] !== true}
            data-testid={`provider-key-clear-${id}`}
            className="px-2 py-1 rounded border border-vibe-border text-xs text-vibe-text-secondary disabled:opacity-50"
          >
            Clear
          </button>
        </div>
        );
      })}
      {status && (
        <div className="text-xs text-vibe-text-secondary" data-testid="provider-keys-status">
          {status}
        </div>
      )}
    </div>
  );
}
